/**
 * Recover from a lazily loaded chunk that no longer exists after a deploy.
 *
 * Every build gives each chunk a content hash, and a deploy replaces the
 * frontend image, so the previous build's chunks are gone (nginx answers 404).
 * index.html is served no-cache, but a tab opened before the deploy still runs
 * the old bundle: the first time it needs a chunk it hasn't loaded yet, the
 * import fails with "Failed to fetch dynamically imported module". The chunks
 * this app loads lazily are mermaid's diagram renderers and layouts (the ER
 * diagram on the profiler's Relationships tab, the Semantic Model Creator),
 * jspdf's html2canvas / purify (PDF export), jszip, and the MDM API client.
 *
 * Vite routes every dynamic import through its preload helper, which
 * dispatches `vite:preloadError` on window. On that event this asks the server
 * for the CURRENT index.html and compares its entry script with the running
 * one. Only when they differ (a new build is live) does it reload the page,
 * which loads the new index.html and its chunks. A chunk that failed for any
 * other reason (a network blip, being offline) changes nothing: the error
 * reaches the caller's own error handling, as before.
 *
 * The reload is guarded through sessionStorage, so a page that keeps failing
 * after a reload (mid rolling deploy, or a chunk missing from the new build
 * too) cannot loop: within RELOAD_GUARD_MS of the last recovery reload the
 * error is left to the caller.
 */

export const RELOAD_GUARD_KEY = "ztd:stale-chunk-reload-at";
export const RELOAD_GUARD_MS = 60_000;

/** True when the page reloaded for a stale chunk less than RELOAD_GUARD_MS
 * ago (a clock that moved backwards since does not count as "recent"). */
function recentlyReloaded(storage: Storage, now: number): boolean {
  const last = Number(storage.getItem(RELOAD_GUARD_KEY) || 0);
  return (
    Number.isFinite(last) &&
    last > 0 &&
    now >= last &&
    now - last < RELOAD_GUARD_MS
  );
}

/**
 * Reload once for a stale chunk. Returns true when a reload was started,
 * false when the guard refused it (and says why on the console).
 */
export function reloadForStaleChunk(
  reload: () => void,
  storage: Storage | null,
  now: number = Date.now(),
): boolean {
  // Without storage there is no way to tell a first failure from a loop.
  if (!storage) {
    console.info(
      "[ztd] stale chunk: no sessionStorage to guard a reload; not reloading",
    );
    return false;
  }
  try {
    if (recentlyReloaded(storage, now)) {
      console.info(
        "[ztd] stale chunk: already reloaded for one in the last minute; not reloading again",
      );
      return false;
    }
    storage.setItem(RELOAD_GUARD_KEY, String(now));
  } catch {
    console.info("[ztd] stale chunk: sessionStorage unusable; not reloading");
    return false;
  }
  reload();
  return true;
}

/** The running build's entry script (``/assets/index-<hash>.js``), or null in
 * the dev server (no hashed entry: nothing to compare). */
export function runningEntry(doc: Document): string | null {
  for (const s of Array.from(
    doc.querySelectorAll("script[type=module][src]"),
  )) {
    const src = new URL((s as HTMLScriptElement).src, doc.baseURI).pathname;
    if (/^\/assets\/index-[^/]+\.js$/.test(src)) return src;
  }
  return null;
}

/** The entry script an index.html names, or null. */
export function entryInHtml(html: string): string | null {
  const m = html.match(
    /<script[^>]*type="module"[^>]*src="([^"]*\/assets\/index-[^"/]+\.js)"/,
  );
  return m ? new URL(m[1], "http://x").pathname : null;
}

/** Has a different build been deployed since this page loaded? Throws when
 * the server can't be asked (offline): then nobody knows, and nothing reloads. */
export async function newBuildDeployed(
  current: string,
  fetchFn: typeof fetch,
): Promise<boolean> {
  const res = await fetchFn("/index.html", { cache: "no-store" });
  if (!res.ok) throw new Error(`index.html: HTTP ${res.status}`);
  const deployed = entryInHtml(await res.text());
  return deployed !== null && deployed !== current;
}

function safeSessionStorage(win: Window): Storage | null {
  try {
    return win.sessionStorage;
  } catch {
    return null;
  }
}

/** Install the window listener once, at startup (main.tsx). */
export function installStaleChunkRecovery(
  win: Window = window,
  deps: {
    reload?: () => void;
    storage?: Storage | null;
    fetch?: typeof fetch;
  } = {},
): void {
  const reload = deps.reload ?? (() => win.location.reload());
  const fetchFn =
    deps.fetch ?? ((...a: Parameters<typeof fetch>) => win.fetch(...a));
  let reloading = false;
  let checking = false;
  win.addEventListener("vite:preloadError", (event) => {
    // A reload is under way: further chunk errors on this page are noise.
    if (reloading) {
      event.preventDefault();
      return;
    }
    const current = runningEntry(win.document);
    if (current === null || checking) return; // dev server, or already asking
    checking = true;
    void newBuildDeployed(current, fetchFn)
      .then((changed) => {
        if (!changed) {
          console.info(
            "[ztd] a chunk failed to load, but this build is still the deployed one; not reloading",
          );
          return;
        }
        const storage =
          deps.storage !== undefined ? deps.storage : safeSessionStorage(win);
        if (reloadForStaleChunk(reload, storage)) {
          reloading = true;
          console.warn(
            "[ztd] the app was redeployed since this page loaded; reloading to the new version",
          );
        }
      })
      .catch((err: unknown) => {
        console.info(
          "[ztd] a chunk failed to load and the deployed version could not be checked; not reloading",
          err,
        );
      })
      .finally(() => {
        checking = false;
      });
  });
}
